import { useTranslation } from 'react-i18next'
import { TextTemplateData } from '../../../../types'
import { useContext } from 'react'
import { ProjectContext } from '../../../../contexts'
import { Alert, Heading, InfoTable, Tag } from '../../../../ui'
import VariableDisplay from '../../../../ui/form/VariableDisplay'

const hasUnicode = (str: string) => {
    // eslint-disable-next-line no-control-regex
    return /[^\u0000-\u007F]/.test(str)
}

export const TextTable = ({ data: { text, media_url, rcs, editor }, locale }: { data: TextTemplateData, locale: string }) => {
    const { t } = useTranslation()
    const [project] = useContext(ProjectContext)
    const isUnicode = hasUnicode(text)
    const segmentLength = isUnicode ? 70 : 160
    const optOutFirstLength = project.text_opt_out?.first?.[locale]?.length ?? 0
    const optOutRegularLength = project.text_opt_out?.regular?.[locale]?.length ?? 0
    const baseLength = (text?.length ?? 0)
    const totalFirstLength = baseLength + optOutFirstLength
    const totalRegularLength = baseLength + optOutRegularLength
    const isHandlebars = text?.includes('{{') ?? false

    const isInteractive = editor === 'interactive'

    const lengthStr = (length: number) => {
        const segments = Math.ceil(length / segmentLength)
        return t('text_characters_segments', { prefix: isHandlebars ? '~' : '', length, segmentLength, segments, count: segments })
    }

    const messageType = isInteractive
        ? t('interactive')
        : media_url
            ? 'MMS'
            : t('simple')

    return <>
        <InfoTable rows={{
            [t('type')]: messageType,
            [t('text')]: text
                ? <VariableDisplay value={text} />
                : <Tag variant="warn">{t('missing')}</Tag>,
            ...(media_url && { [t('media_url')]: media_url }),
            ...(isInteractive && rcs?.rich_card && {
                [t('text_interactive_card')]: rcs.rich_card.card_content.title
                    ? <VariableDisplay value={rcs.rich_card.card_content.title} />
                    : t('untitled'),
            }),
            ...(rcs?.suggestions?.length && {
                [t('text_interactive_buttons')]: `${rcs.suggestions.length} ${t('rcs_action_count')}`,
            }),
        }} />
        <Heading title={t('send_details')} size="h4" />
        {isInteractive && <Alert variant="plain" title={t('note')} body={t('text_interactive_fallback_desc')} />}
        {totalRegularLength > segmentLength && <Alert variant="plain" title={t('note')} body={t('text_segment_warning', { multiplier: Math.ceil(totalRegularLength / segmentLength) })} />}
        {isUnicode && <Alert variant="warn" title={t('text_warn')} body={t('text_segment_unicode_warning')} />}
        <InfoTable rows={{
            ...(isInteractive && { [t('text_interactive')]: t('rcs_no_segment_limits') }),
            [`${isInteractive ? `${t('text_fallback')} - ` : ''}${t('existing_user_length')}`]: lengthStr(totalRegularLength),
            [`${isInteractive ? `${t('text_fallback')} - ` : ''}${t('new_user_length')}`]: lengthStr(totalFirstLength),
        }} />
    </>
}
